import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Paper,
  Table,
  TableBody,
  TableContainer,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import FormRow from './FormRow';
import LiveAircraftUsagePanel from './LiveAircraftUsagePanel';
import LiveTlbUsageMappingEditor from '../LiveTlbUsageMappingEditor';
import { getTimezoneLabel } from '../../utils/timezoneUtils';
import { ROLES } from '../../constants/roles';

const LIVE_MAP_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.OPERATIONS_MANAGER,
  ROLES.OPERATIONS_EXECUTIVE,
  ROLES.ACCOUNTABLE_MANAGER
];

const WarrantyAndTSNTab = ({
  formData,
  setFormData,
  handleNestedChange,
  displayTimezone,
  liveUsage,
  liveUsageLoading,
  liveUsageError,
  user
}) => {
  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const canEditLiveMapping = LIVE_MAP_ROLES.includes(user?.role);

  // Helper function to format date for TSN (YYYY-MM-DD format)
  const formatDateForTSN = (dateStr) => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    // Try to parse and format
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const addTSNField = () => {
    // Get Manufacturing Date from Basic Information
    const manufacturingDate = formatDateForTSN(formData.dateOfManufacture);
    
    // Create keyValues array with Manufacturing Date auto-populated if available
    const keyValues = [
      { period: 'Hours', value: '' },
      { period: 'Manufacturing Date', value: manufacturingDate },
      { period: 'Landings', value: '' }
    ];

    setFormData(prev => ({
      ...prev,
      tsn: [
        ...prev.tsn,
        {
          asOnDate: '',
          keyValues: keyValues,
          additionalFields: {}
        }
      ]
    }));
  };

  // Auto-populate Manufacturing Date in existing TSN entries when dateOfManufacture changes
  // Only updates entries where Manufacturing Date is empty or missing
  useEffect(() => {
    const manufacturingDate = formatDateForTSN(formData.dateOfManufacture);
    
    if (!manufacturingDate || !formData.tsn || formData.tsn.length === 0) {
      return; // No manufacturing date or no TSN entries, nothing to do
    }
    
    // Check if any TSN entry needs updating (has empty Manufacturing Date)
    const needsUpdate = formData.tsn.some(tsnItem => {
      const keyValues = tsnItem.keyValues || [];
      const manufacturingDateKV = keyValues.find(kv => kv.period === 'Manufacturing Date');
      return !manufacturingDateKV || !manufacturingDateKV.value;
    });
    
    if (!needsUpdate) {
      return; // All TSN entries already have Manufacturing Date, no update needed
    }
    
    // Update TSN entries that need Manufacturing Date populated
    setFormData(prev => {
      const updatedTSN = prev.tsn.map(tsnItem => {
        const keyValues = (tsnItem.keyValues || []).map(kv => {
          // If this is Manufacturing Date and it's empty, auto-populate it
          if (kv.period === 'Manufacturing Date' && !kv.value) {
            return { ...kv, value: manufacturingDate };
          }
          return kv;
        });
        
        // If Manufacturing Date keyValue doesn't exist, add it
        const hasManufacturingDate = keyValues.some(kv => kv.period === 'Manufacturing Date');
        if (!hasManufacturingDate) {
          keyValues.push({ period: 'Manufacturing Date', value: manufacturingDate });
        }
        
        return { ...tsnItem, keyValues };
      });
      
      return { ...prev, tsn: updatedTSN };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dateOfManufacture]); // Only run when dateOfManufacture changes

  const removeTSNField = (index) => {
    setFormData(prev => ({
      ...prev,
      tsn: prev.tsn.filter((_, i) => i !== index)
    }));
  };

  const updateTSNField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tsn: prev.tsn.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const addTSNKeyValue = (index) => {
    setFormData(prev => ({
      ...prev,
      tsn: prev.tsn.map((item, i) =>
        i === index
          ? {
              ...item,
              keyValues: [...(item.keyValues || []), { period: '', value: '' }]
            }
          : item
      )
    }));
  };

  const updateTSNKeyValue = (tsnIndex, kvIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      tsn: prev.tsn.map((item, i) =>
        i === tsnIndex
          ? {
              ...item,
              keyValues: (item.keyValues || []).map((kv, j) =>
                j === kvIndex ? { ...kv, [field]: value } : kv
              )
            }
          : item
      )
    }));
  };

  const removeTSNKeyValue = (tsnIndex, kvIndex) => {
    setFormData(prev => ({
      ...prev,
      tsn: prev.tsn.map((item, i) =>
        i === tsnIndex
          ? {
              ...item,
              keyValues: (item.keyValues || []).filter((_, j) => j !== kvIndex)
            }
          : item
      )
    }));
  };

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        variant="outlined" 
        sx={{ 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: 'primary.main', 
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Warranty Details</Typography>
        </Box>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(0, 0, 0, 0.08)' } }}>
          <TableBody>
            <FormRow label="Is Aircraft under warranty?">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.warranty.isUnderWarranty}
                onChange={handleNestedChange('warranty', 'isUnderWarranty')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </FormRow>
            {formData.warranty.isUnderWarranty === 'Yes' && (
              <>
                <FormRow label={tzLabel ? `Warranty Start Date (${tzLabel})` : 'Warranty Start Date'}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={formData.warranty.warrantyStartDate}
                    onChange={handleNestedChange('warranty', 'warrantyStartDate')}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                  />
                </FormRow>
                <FormRow label={tzLabel ? `Warranty End Date (${tzLabel})` : 'Warranty End Date'}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={formData.warranty.warrantyEndDate}
                    onChange={handleNestedChange('warranty', 'warrantyEndDate')}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                  />
                </FormRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Tech Log Page
          </Typography>
        </Box>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(0, 0, 0, 0.08)' } }}>
          <TableBody>
            <FormRow label="Tech Log Page Format">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.warranty.techLogPage}
                onChange={handleNestedChange('warranty', 'techLogPage')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                <MenuItem value="Single Sector">Single Sector</MenuItem>
                <MenuItem value="Multiple Sector">Multiple Sector</MenuItem>
                <MenuItem value="Only Airbone Time Entry">Only Airbone Time Entry</MenuItem>
              </TextField>
            </FormRow>
          </TableBody>
        </Table>
      </TableContainer>

      <LiveAircraftUsagePanel
        liveUsage={liveUsage}
        liveUsageLoading={liveUsageLoading}
        liveUsageError={liveUsageError}
        displayTimezone={displayTimezone}
      />

      {canEditLiveMapping && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Live TLB field mapping (this aircraft)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Optional override of tenant defaults (Client Settings → Live TLB mapping). Uncheck to clear the override and use
            tenant defaults only.
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.liveTlbMappingUseAircraftOverride}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    liveTlbMappingUseAircraftOverride: on,
                    liveTlbUsageMappingOverride: on
                      ? prev.liveTlbUsageMappingOverride || {}
                      : prev.liveTlbUsageMappingOverride
                  }));
                }}
              />
            }
            label="Use custom live TLB field mapping for this aircraft"
          />
          {formData.liveTlbMappingUseAircraftOverride ? (
            <Box sx={{ mt: 2 }}>
              <LiveTlbUsageMappingEditor
                value={formData.liveTlbUsageMappingOverride || {}}
                onChange={(next) =>
                  setFormData((prev) => ({ ...prev, liveTlbUsageMappingOverride: next }))
                }
                idPrefix="acft-tlb"
              />
            </Box>
          ) : null}
        </Paper>
      )}

      <Paper 
        variant="outlined" 
        sx={{ 
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Times Since New Values (TSN) as on Date</Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={addTSNField}
            size="small"
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            Add TSN Entry
          </Button>
        </Box>
        {formData.tsn.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              No TSN entries. Click "Add TSN Entry" to add one.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            {formData.tsn.map((tsnItem, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(25, 118, 210, 0.02)',
                  borderColor: 'rgba(25, 118, 210, 0.2)',
                  '&:last-child': { mb: 0 }
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                    TSN Entry {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeTSNField(index)}
                    color="error"
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(211, 47, 47, 0.08)'
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <FormRow label={tzLabel ? `As On Date (${tzLabel})` : 'As On Date'}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={tsnItem.asOnDate}
                          onChange={(e) => updateTSNField(index, 'asOnDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                        />
                      </FormRow>
                      <FormRow label="Period / Value Pairs" fullWidth>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {(tsnItem.keyValues || []).map((kv, kvIndex) => (
                            <Box
                              key={kvIndex}
                              sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
                            >
                              <TextField
                                size="small"
                                sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                                value={kv.period}
                                onChange={(e) =>
                                  updateTSNKeyValue(index, kvIndex, 'period', e.target.value)
                                }
                                placeholder="Period (e.g., Hours, Manufacturing Date, Landings)"
                              />
                              <TextField
                                size="small"
                                sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                                value={kv.value}
                                onChange={(e) =>
                                  updateTSNKeyValue(index, kvIndex, 'value', e.target.value)
                                }
                                placeholder="Value (e.g., 1125.54, 30-Oct-2009, 3278)"
                              />
                              <IconButton
                                size="small"
                                onClick={() => removeTSNKeyValue(index, kvIndex)}
                                color="error"
                                sx={{
                                  '&:hover': {
                                    bgcolor: 'rgba(211, 47, 47, 0.08)'
                                  }
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Box>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => addTSNKeyValue(index)}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                px: 0
                              }}
                            >
                              + Add Period / Value
                            </Button>
                          </Box>
                        </Box>
                      </FormRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WarrantyAndTSNTab;

